import React from 'react';
import { Divider, Popover, Typography, message } from 'antd';
import { MiddleEllipsis } from '@components/ResizableTable';
import type { BuildSpec, BuildType, JobItem } from '@apis/simulation';

/** 字段缺失判断：null/undefined/空白/字面 ``"null" | "undefined"`` 一律视为缺失。 */
const isMissing = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim().toLowerCase();
  return s === '' || s === 'null' || s === 'undefined';
};

/** 是否为云端 OBS / S3 / BOS 路径。仅在云路径时启用「完整路径」复制能力。 */
const isCloudUrl = (s: unknown): boolean => {
  if (typeof s !== 'string') return false;
  const t = s.trim().toLowerCase();
  return t.startsWith('bos://') || t.startsWith('s3://') || t.startsWith('obs://');
};

/** 解析 commit_id 多模块串：``"repo1:val1,repo2:val2"`` → ``{repo1: val1, ...}``。 */
const parseCommitIdString = (s: string | null | undefined): Record<string, string> | null => {
  if (isMissing(s)) return null;
  const map: Record<string, string> = {};
  for (const pair of String(s).split(',')) {
    const [repo, value] = pair.split(':').map((x) => x.trim());
    if (!isMissing(repo) && !isMissing(value)) map[repo] = value;
  }
  return Object.keys(map).length ? map : null;
};

/**
 * 老 ``algorithmVersion`` 字段解析：
 *
 * - JSON 串 ``{"commit_id": "repo1:val,repo2:val"}`` → 走 ``parseCommitIdString``。
 * - 普通字符串（中台版本号 / OBS 路径）→ ``isCommitId=false``，原文展示。
 *
 * 解析失败一律按原文展示，不抛异常。
 */
const parseAlgorithmVersion = (
  text: string,
): { isCommitId: boolean; commitMap?: Record<string, string>; raw: string } => {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && parsed.commit_id) {
      const map = parseCommitIdString(parsed.commit_id);
      if (map) return { isCommitId: true, commitMap: map, raw: text };
    }
  } catch {
    // not JSON
  }
  return { isCommitId: false, raw: text };
};

interface AlgorithmVersionCellProps {
  /** 后端 ``algorithm_version`` 列原文 = 完整包路径（云端 URL）。
   *  作为 hover Tooltip / 复制按钮的「全路径」数据源。 */
  text?: string | null;
  /** 空值占位，默认 ``-`` */
  emptyText?: string;
  /** 新协议入口：DB 中的 ``build_type``，配合 ``buildSpec`` 使用。 */
  buildType?: BuildType | null;
  /** 新协议入口：DB 中的 ``build_spec`` jsonb 反序列化对象。 */
  buildSpec?: BuildSpec | null;
  /** bazel 编译 job 专用：branch_dp_info 原文（``module:branch,...``），
   *  与 ``bazelModuleCommits`` 一起在完整包路径弹层里展示 ``module branch(commit)``。 */
  bazelBranchDpInfo?: string | null;
  /** bazel 编译 job 专用：``module → commitSHA`` 映射。 */
  bazelModuleCommits?: Record<string, string> | null;
  /** bazel 编译 job 专用：基线版本（base_version），展示在分支信息块上面。 */
  bazelBaseVersion?: string | null;
  /** 算法版本「基准版本」递归下钻的追溯入口版本号（后端 traceEntryVersion）。
   *  非空且传了 ``onTrace`` 时，基线版本号 / 完整包路径行做成可点击「追溯」入口。 */
  traceEntryVersion?: string | null;
  /** 点击追溯入口回调：上抛 ``traceEntryVersion`` 给页面级 VersionTraceModal 打开。
   *  不传则不渲染任何追溯链接（纯展示）。 */
  onTrace?: (version: string) => void;
}

/** 解析 branch_dp_info → ``{module: branch}``。
 *
 * 兼容两种后端来源:
 * - ``bazel_build_result.branch_dp_info``(编译回调后写):``module:branch,module2:branch2`` 冒号串
 * - ``external.bazel_inputs.branch_dp_info``(创建时用户提交原文,fallback 源):
 *   ``module branch\nmodule2 branch2`` 空格分隔多行,由前端
 *   ``CreateSceneQcJobModal.mergeBranchDpInfo`` / kit ``_merge_branch_dp_info`` 产出
 *
 * 组分隔用逗号/换行,pair 分隔用冒号/空格——与创建弹框及 kit CLI 保持同一套字段规约。
 */
const parseBranchDpInfo = (s: string | null | undefined): Record<string, string> => {
  const map: Record<string, string> = {};
  if (isMissing(s)) return map;
  for (const entry of String(s).split(/[,\n]+/)) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const parts = trimmed.split(/[:\s]+/);
    if (parts.length < 2) continue;
    const mod = parts[0]?.trim();
    const branch = parts.slice(1).join(' ').trim();
    if (mod && branch) map[mod] = branch;
  }
  return map;
};

/** 合并 branch_dp_info 与 module_commits，产出 ``[{module, branch?, commit?}]``（按模块名排序）。 */
const buildBazelModuleRows = (
  branchDpInfo: string | null | undefined,
  moduleCommits: Record<string, string> | null | undefined,
): { module: string; branch?: string; commit?: string }[] => {
  const branchMap = parseBranchDpInfo(branchDpInfo);
  const commitMap = moduleCommits || {};
  const modules = Array.from(new Set([...Object.keys(branchMap), ...Object.keys(commitMap)])).sort();
  return modules.map((module) => ({
    module,
    branch: branchMap[module],
    commit: commitMap[module],
  }));
};

/** bazel 编译 job 的「标题: 值（带复制按钮）」单行展示块。复用给基线版本号 / 当前版本号。 */
const LabeledCopyableRow: React.FC<{ label: string; value: string; copyHint?: string }> = ({
  label, value, copyHint,
}) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 4 }}>
    <span style={{ fontWeight: 500, minWidth: 88, color: '#333' }}>{label}:</span>
    <Typography.Text
      copyable={{
        text: value,
        tooltips: [copyHint || `复制${label}`, '已复制'],
        onCopy: () => message.success(`已复制${label}`),
      }}
      style={{ color: '#666', flex: 1, minWidth: 0 }}
    >
      <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{value}</code>
    </Typography.Text>
  </div>
);


/** 「标题: 值（可复制） + 追溯链接」行。用于基线版本号——值可复制、点「追溯」下钻。
 *  ``onTrace`` / ``traceVersion`` 任一缺失时退化为无链接（仅可复制）。 */
const LabeledTraceRow: React.FC<{
  label: string;
  value: string;
  traceVersion?: string | null;
  onTrace?: (version: string) => void;
}> = ({ label, value, traceVersion, onTrace }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 4 }}>
    <span style={{ fontWeight: 500, minWidth: 88, color: '#333' }}>{label}:</span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <Typography.Text
        copyable={{
          text: value,
          tooltips: [`复制${label}`, '已复制'],
          onCopy: () => message.success(`已复制${label}`),
        }}
        style={{ color: '#666' }}
      >
        <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{value}</code>
      </Typography.Text>
      {onTrace && traceVersion && (
        <a
          style={{ color: '#5e6ad2', marginLeft: 8, fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={() => onTrace(traceVersion)}
        >
          追溯 →
        </a>
      )}
    </span>
  </div>
);


/** bazel 编译 job 的「模块分支 / Commit」展示块：每行 ``module branch (commit12)``。
 *  无任何模块行（用户未填 branch_dp_info 且未解析出 commit）→ 仍渲染块头并给出
 *  「用户未填分支信息」灰字提示，便于读者明确「没有分支变更」而不是「数据缺失」。 */
const BazelModuleBlock: React.FC<{
  branchDpInfo?: string | null;
  moduleCommits?: Record<string, string> | null;
}> = ({ branchDpInfo, moduleCommits }) => {
  const rows = buildBazelModuleRows(branchDpInfo, moduleCommits);
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontWeight: 500, color: '#333', marginBottom: 4 }}>模块分支 / Commit:</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
          用户未填分支信息
        </div>
      ) : (
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          {rows.map((r) => (
            <div key={r.module} style={{ display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 500, minWidth: 120, color: '#333', wordBreak: 'break-all' }}>
                {r.module}
              </span>
              <span style={{ color: '#666', wordBreak: 'break-all' }}>
                {r.branch || '-'}
                {r.commit && (
                  <span style={{ color: '#999' }}> ({r.commit.slice(0, 12)})</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** 内部归一化结果：编译模式 → ``commitMap``；中台/老数据 → ``plainText``；空 → ``empty``。 */
type Normalized =
  | { kind: 'commit'; commitMap: Record<string, string> }
  | { kind: 'plain'; plainText: string }
  | { kind: 'empty' };

/** 把"新协议（buildSpec）"与"老协议（text）"统一规约到展示状态机。新协议优先。 */
const normalize = (props: AlgorithmVersionCellProps): Normalized => {
  const { buildType, buildSpec, text } = props;

  if (buildType === 'compile_platform' && buildSpec && buildSpec.type === 'compile_platform') {
    const map = parseCommitIdString(buildSpec.commitId);
    if (map) return { kind: 'commit', commitMap: map };
  }
  if (
    (buildType === 'package_neodata' && buildSpec && buildSpec.type === 'package_neodata') ||
    (buildType === 'package_bazel' && buildSpec && buildSpec.type === 'package_bazel')
  ) {
    if (!isMissing(buildSpec.algorithmVersion)) {
      return { kind: 'plain', plainText: buildSpec.algorithmVersion };
    }
  }

  // 老数据兜底：复用 text 字段解析
  if (!isMissing(text)) {
    const parsed = parseAlgorithmVersion(String(text));
    if (parsed.isCommitId && parsed.commitMap) {
      return { kind: 'commit', commitMap: parsed.commitMap };
    }
    return { kind: 'plain', plainText: parsed.raw };
  }
  return { kind: 'empty' };
};

/** Popover 内统一的「完整包路径」展示行：URL 折行 + 一键复制（+ 可选「追溯」链接）。
 *  ``onTrace`` / ``traceVersion`` 齐备时，行尾出现「追溯 →」链接，点击以该版本下钻
 *  （满足需求：完整包路径做成可点击对象，弹窗展示其中版本号对应的详情）。 */
const PackageUrlBlock: React.FC<{
  url: string;
  traceVersion?: string | null;
  onTrace?: (version: string) => void;
}> = ({ url, traceVersion, onTrace }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
    <span style={{ fontWeight: 500, minWidth: 88, color: '#333' }}>完整包路径:</span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <Typography.Text
        copyable={{
          text: url,
          tooltips: ['复制完整包路径', '已复制'],
          onCopy: () => message.success('已复制完整包路径'),
        }}
        style={{ color: '#666' }}
      >
        <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{url}</code>
      </Typography.Text>
      {onTrace && traceVersion && (
        <a
          style={{ color: '#5e6ad2', marginLeft: 8, fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={() => onTrace(traceVersion)}
        >
          追溯 →
        </a>
      )}
    </span>
  </div>
);

/**
 * 中台模式单元格统一布局：
 *
 * - ``copyUrl`` 存在时：整行作为 Popover 触发器，hover 弹出面板展示完整包路径 +
 *   复制按钮。**为避免与 ``MiddleEllipsis`` 自带的 Tooltip 形成双层浮层重叠**，
 *   此分支改用 CSS 单行省略，所有"看全路径"的能力交给 Popover 面板。
 * - ``copyUrl`` 不存在时（非云端 URL）：退化为 ``MiddleEllipsis``，由其内置
 *   Tooltip 在截断场景下展示完整原文。
 */
const PackageCellLayout: React.FC<{
  plainText: string;
  copyUrl: string | null;
  /** Popover 内追加的额外节点（如 bazel 编译 job 的「模块分支 / Commit」块）。 */
  extra?: React.ReactNode;
  /** ``true`` 时把 ``extra`` 放在「完整包路径」上方（bazel 编译模式：先看分支再看包名）。
   *  默认 ``false`` 保持中台模式现有布局。 */
  extraAbove?: boolean;
  /** neodata 场景：完整包路径行的追溯入口版本号（点「追溯」以自身版本下钻）。
   *  bazel 场景不传（其基线追溯入口在「基线版本号」行）。 */
  packageTraceVersion?: string | null;
  onTrace?: (version: string) => void;
}> = ({ plainText, copyUrl, extra, extraAbove = false, packageTraceVersion, onTrace }) => {
  // 既无完整包路径又无额外信息 → 退化为纯文本 + 省略 Tooltip，不挂 Popover
  if (!copyUrl && !extra) return <MiddleEllipsis text={plainText} />;
  return (
    <Popover
      placement="topLeft"
      styles={{ root: { maxWidth: 520 } }}
      title="完整包路径"
      content={
        <div style={{ maxWidth: 520 }}>
          {extraAbove ? (
            <>
              {extra}
              {extra && copyUrl && <Divider style={{ margin: '10px 0' }} />}
              {copyUrl && (
                <PackageUrlBlock url={copyUrl} traceVersion={packageTraceVersion} onTrace={onTrace} />
              )}
            </>
          ) : (
            <>
              {copyUrl && (
                <PackageUrlBlock url={copyUrl} traceVersion={packageTraceVersion} onTrace={onTrace} />
              )}
              {extra}
            </>
          )}
        </div>
      }
    >
      <div
        style={{
          cursor: 'pointer',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {plainText}
      </div>
    </Popover>
  );
};

/**
 * 算法版本表格单元格：
 *
 * - **中台模式（plain）**：列里展示 ``buildSpec.algorithmVersion``（用户原始输入：
 *   可能是简短版本号或完整路径），列尾图标 hover 看后端 ``algorithm_version`` 列
 *   存的完整 OBS 路径，点击复制全路径。
 * - **编译模式（commit）**：``N repos`` 入口 + Popover 展示模块版本详情；Popover 内
 *   底部展示后端 ``algorithm_version`` 列（云端 URL）作为编译包路径，hover 看全、
 *   一键复制。前端不再二次拼装路径。
 * - **空值**：渲染 ``emptyText``（默认 ``-``）。
 *
 * 数据来源优先级：``buildType`` + ``buildSpec``（新协议）> ``text``（老数据兜底）。
 */
const AlgorithmVersionCell: React.FC<AlgorithmVersionCellProps> = (props) => {
  const { emptyText = '-', text } = props;
  const result = normalize(props);

  if (result.kind === 'empty') return <>{emptyText}</>;

  // 完整包路径直接取后端 algorithm_version 列原文，仅当其为云端 URL 时启用复制
  const copyUrl = isCloudUrl(text) ? String(text).trim() : null;

  if (result.kind === 'plain') {
    // bazel 编译 job：在完整包路径弹层里追加信息（顺序）：
    //   当前版本号 → 分支信息 → 基线版本号（可点「追溯」下钻基线链）→ 完整包路径
    const isBazel = props.buildType === 'package_bazel';
    const isNeodata = props.buildType === 'package_neodata';
    const baseVersionStr = (props.bazelBaseVersion || '').trim();
    const currentVersionStr = result.plainText;
    const bazelExtra = isBazel ? (
      <>
        {currentVersionStr && (
          <LabeledCopyableRow label="当前版本号" value={currentVersionStr} />
        )}
        <BazelModuleBlock
          branchDpInfo={props.bazelBranchDpInfo}
          moduleCommits={props.bazelModuleCommits}
        />
        {baseVersionStr && (
          // 基线版本号：可复制 + 点「追溯」以 traceEntryVersion（后端=归一化基线）下钻基线链。
          <LabeledTraceRow
            label="基线版本号"
            value={baseVersionStr}
            traceVersion={props.traceEntryVersion}
            onTrace={props.onTrace}
          />
        )}
      </>
    ) : null;

    return (
      <PackageCellLayout
        plainText={result.plainText}
        copyUrl={copyUrl}
        extra={bazelExtra}
        // bazel 模式：分支/当前/基线版本号在上、完整包路径在下；中台模式保持原顺序
        extraAbove={isBazel}
        // neodata：完整包路径行做成追溯入口（以自身归一化版本号下钻各仓 commit）
        packageTraceVersion={isNeodata ? props.traceEntryVersion : null}
        onTrace={props.onTrace}
      />
    );
  }

  const entries = Object.entries(result.commitMap);
  const summary = `${entries.length} repos (branch/commit_id)`;

  return (
    <Popover
      title="Commit ID 详情"
      placement="right"
      content={
        <div style={{ maxWidth: 520, fontSize: 13, lineHeight: 1.8 }}>
          {entries.map(([repo, value]) => (
            <div key={repo} style={{ display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 500, minWidth: 120, color: '#333' }}>{repo}:</span>
              <span style={{ color: '#666', wordBreak: 'break-all' }}>{value}</span>
            </div>
          ))}

          {copyUrl && (
            <>
              <Divider style={{ margin: '10px 0' }} />
              <PackageUrlBlock url={copyUrl} />
            </>
          )}
        </div>
      }
    >
      <a
        style={{
          color: '#5e6ad2',
          cursor: 'pointer',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {summary}
      </a>
    </Popover>
  );
};

/**
 * 从一条 ``JobItem`` 渲染算法版本单元格的**统一出口**。
 *
 * 直接手写 ``<AlgorithmVersionCell />`` 需要逐个铺开 7 个 prop，各页面复制粘贴时
 * 极易漏传 ``bazelBranchDpInfo`` / ``bazelModuleCommits`` / ``bazelBaseVersion``——
 * 漏传的后果不是报错而是弹层里静默显示「用户未填分支信息」（Daily 页曾如此）。
 * 新增调用点一律走本函数，让「JobItem → 单元格」的字段映射只存在一份。
 *
 * ``text`` 默认取 ``record.algorithmVersion``；个别页面（如任务详情）需要用别处
 * 拿到的版本原文覆盖时传 ``overrides.text``。``record`` 允许为空（详情页数据未到位），
 * 此时退化为只渲染 ``overrides.text`` / 占位符。
 */
export const renderAlgorithmVersionCell = (
  record: JobItem | null | undefined,
  onTrace?: (version: string) => void,
  overrides?: { text?: string | null; emptyText?: string },
): React.ReactElement => (
  <AlgorithmVersionCell
    text={overrides?.text !== undefined ? overrides.text : record?.algorithmVersion}
    emptyText={overrides?.emptyText}
    buildType={record?.buildType}
    buildSpec={record?.buildSpec}
    // 编译回调之前 ``bazelBranchDpInfo`` 为 null(``bazel_build_result.branch_dp_info``
    // 由 workflow 回调时才写,附带各模块 commit)。此时 fallback 到用户提交时的
    // ``external.bazel_inputs.branch_dp_info``,让「编译中」也能看到分支列表;
    // 回调后 ``bazelBranchDpInfo`` 有值(附 commit)会自动覆盖 fallback。
    bazelBranchDpInfo={record?.bazelBranchDpInfo ?? record?.bazelInputs?.branch_dp_info ?? null}
    bazelModuleCommits={record?.bazelModuleCommits}
    bazelBaseVersion={record?.bazelInputs?.base_version}
    traceEntryVersion={record?.traceEntryVersion}
    onTrace={onTrace}
  />
);

export default AlgorithmVersionCell;
